/* Coordinación de las barras: un solo listener de scroll, sin bucles continuos. */
(() => {
  if (window.rvStickyLayout) return;
  const root = document.documentElement;
  const mobile = window.matchMedia('(max-width: 1024px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const listeners = new Set();
  let frame = 0;
  let headerHeight = 0;
  const editable = 'textarea, [contenteditable="true"], input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"])';
  const request = () => {
    if (frame || !listeners.size) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const state = {
        mobile: mobile.matches,
        headerHeight,
        blocked: document.body.matches('.overflow-hidden, .overflow-hidden-tablet, .overflow-hidden-mobile, .fixed-position, .rv-sticky-input')
      };
      listeners.forEach(listener => listener(state));
    });
  };
  const onFocus = () => {
    document.body.classList.toggle('rv-sticky-input', mobile.matches && Boolean(document.activeElement?.matches(editable)));
    request();
  };
  window.rvStickyLayout = {
    request,
    subscribe(listener) { listeners.add(listener); request(); return () => listeners.delete(listener); },
    setHeaderHeight(height) {
      height = Math.ceil(height);
      if (height === headerHeight) return;
      headerHeight = height;
      root.style.setProperty('--rv-header-height', `${height}px`);
      request();
    }
  };
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });
  window.addEventListener('pageshow', request);
  mobile.addEventListener('change', onFocus);
  document.addEventListener('focusin', onFocus);
  document.addEventListener('focusout', () => queueMicrotask(onFocus));
  const bodyObserver = new MutationObserver(request);
  bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  window.visualViewport?.addEventListener('resize', request, { passive: true });

  const setText = (element, text) => { if (element && element.textContent !== text) element.textContent = text; };
  const setVisible = (element, visible) => {
    if (element.classList.contains('show-modal') === visible) return;
    element.classList.toggle('show-modal', visible);
    element.setAttribute('aria-hidden', String(!visible));
    element.toggleAttribute('inert', !visible);
  };

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      if (!this.section) return;
      root.classList.add('rv-sticky-header-enabled');
      this.menu = this.querySelector('.header__desktop .header-tiles-container');
      this.nav = this.querySelector('.rv-desktop-nav');
      this.hint = this.querySelector('[data-rv-menu-hint]');
      this.toggle = this.querySelector('[data-rv-menu-toggle]');
      this.compact = false;
      this.menuOpen = false;
      this.animations = new Set();
      if (this.menu && this.nav && this.hint && this.toggle) {
        this.restoring = this.section.hasAttribute('data-rv-menu-restore');
        for (const state of ['ready', 'animate', 'compact', 'open']) this.removeAttribute(`data-rv-menu-${state}`);
        this.hint.hidden = true;
        this.setMenuOpen(false);
        // La referencia permanece en el flujo; el header sticky no sirve como umbral.
        this.marker = document.createElement('div');
        this.marker.className = 'rv-header-scroll-marker';
        this.marker.setAttribute('aria-hidden', 'true');
        this.section.before(this.marker);
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.toggle.addEventListener('click', () => this.setMenuOpen(!this.menuOpen), options);
        document.addEventListener('keydown', event => {
          if (event.key !== 'Escape' || !this.menuOpen) return;
          event.preventDefault();
          this.toggle.focus({ preventScroll: true });
          this.setMenuOpen(false);
        }, options);
        document.addEventListener('pointerdown', event => {
          if (!this.menuOpen || this.contains(event.target)) return;
          if (this.menu.contains(document.activeElement)) this.toggle.focus({ preventScroll: true });
          this.setMenuOpen(false);
        }, options);
        document.addEventListener('focusin', event => {
          if (this.menuOpen && !this.contains(event.target)) this.setMenuOpen(false);
        }, options);
        reducedMotion.addEventListener('change', () => {
          if (reducedMotion.matches) this.stopAnimations();
        }, options);
        // Guardar solo al salir de la página, nunca en cada evento de scroll.
        window.addEventListener('pagehide', () => this.saveReloadState(), options);
        // Medir las tarjetas antes de sacarlas del flujo evita un salto al iniciar.
        this.measure();
        this.setAttribute('data-rv-menu-ready', '');
        this.measure();
        if (this.restoring) this.setCompact(true);
        else this.updateMenu({ mobile: mobile.matches, blocked: false });
        this.section.removeAttribute('data-rv-menu-restore');
        this.unsubscribe = window.rvStickyLayout.subscribe(state => this.updateMenu(state));
        if (this.restoring) this.watchRestoreInput();
        else this.enableAnimations();
      }
      this.resizeObserver = new ResizeObserver(() => { this.measure(); request(); });
      this.resizeObserver.observe(this.section);
      if (this.marker) this.resizeObserver.observe(this.menu);
      this.measure();
    }
    enableAnimations() {
      cancelAnimationFrame(this.readyFrame);
      this.readyFrame = requestAnimationFrame(() => this.setAttribute('data-rv-menu-animate', ''));
    }
    watchRestoreInput() {
      // Si el usuario decide desplazarse antes de que el navegador restaure la
      // página, su acción tiene prioridad (también Home o la rueda en scrollY=0).
      this.restoreInputController = new AbortController();
      const options = { signal: this.restoreInputController.signal, passive: true };
      const release = event => {
        if (event.defaultPrevented) return;
        if (event.type === 'wheel' && !event.deltaY) return;
        if (event.type === 'keydown' &&
            (!['Home', 'End', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', ' '].includes(event.key) ||
             event.target.closest?.('input, textarea, select, [contenteditable="true"]'))) return;
        this.finishRestore();
        request();
      };
      window.addEventListener('wheel', release, options);
      window.addEventListener('touchmove', release, options);
      window.addEventListener('keydown', release, options);
    }
    finishRestore() {
      this.restoring = false;
      this.restoreInputController?.abort();
      this.restoreInputController = null;
      this.enableAnimations();
    }
    saveReloadState() {
      try {
        const distance = -this.marker.getBoundingClientRect().top;
        if (mobile.matches || distance <= 24 || (!this.compact && distance <= this.fullHeight)) {
          sessionStorage.removeItem('rv-sticky-header');
          return;
        }
        this.measure();
        const headerHeight = this.section.getBoundingClientRect().height - this.nav.getBoundingClientRect().height +
          parseFloat(getComputedStyle(this.hint).height);
        sessionStorage.setItem('rv-sticky-header', JSON.stringify({
          url: location.href, width: innerWidth, menuHeight: this.menuHeight, headerHeight: Math.ceil(headerHeight)
        }));
      } catch (_) { /* No depender del almacenamiento para navegar o buscar. */ }
    }
    measure() {
      const height = this.section.getBoundingClientRect().height;
      this.fullHeight = height;
      if (this.marker && !mobile.matches) {
        // Ignorar las diferencias de precisión de las tarjetas transformadas.
        const menuHeight = Math.round(this.menu.getBoundingClientRect().height * 64) / 64;
        const hintHeight = parseFloat(getComputedStyle(this.hint).height);
        const reserve = Math.max(0, menuHeight - hintHeight);
        // El umbral usa siempre la altura del menú completo.
        this.fullHeight = height - this.nav.getBoundingClientRect().height + menuHeight;
        if (this.menuHeight !== menuHeight || this.menuReserve !== reserve) {
          this.menuHeight = menuHeight;
          this.menuReserve = reserve;
          this.section.style.setProperty('--rv-header-menu-height', `${menuHeight}px`);
          this.section.style.setProperty('--rv-header-menu-reserve', `${reserve}px`);
        }
      }
      window.rvStickyLayout.setHeaderHeight(height);
    }
    updateMenu(state) {
      if (this.restoring) {
        // load/pageshow pueden ocurrir con scrollY=0, antes de la restauración.
        // Conservar el estado guardado hasta el scroll real o una acción del usuario.
        if (!state.mobile && scrollY === 0) return;
        this.finishRestore();
      }
      if (state.mobile) {
        this.stopAnimations();
        this.setCompact(false);
        return;
      }
      if (state.blocked) return;
      const distance = -this.marker.getBoundingClientRect().top;
      if (this.compact) {
        if (distance <= 24) this.setCompact(false);
      } else if (distance > this.fullHeight && !this.menu.contains(document.activeElement)) {
        this.setCompact(true);
      }
    }
    setCompact(compact) {
      if (compact === this.compact) return;
      this.measure();
      const animate = this.hasAttribute('data-rv-menu-animate') && !mobile.matches && !reducedMotion.matches;
      // Capturar la posición visual antes de actualizar el offset del header.
      // Así la imagen fija del producto tampoco salta al cambiar de altura.
      const positions = animate ? Array.from(document.querySelectorAll('.product__media-sticky, .product__right-column-sticky'), element => ({
        element, top: element.getBoundingClientRect().top
      })) : [];
      this.stopAnimations();
      const restoreFocus = !compact && this.hint.contains(document.activeElement);
      this.compact = compact;
      this.toggleAttribute('data-rv-menu-compact', compact);
      this.hint.hidden = !compact;
      this.setMenuOpen(false);
      // Mover el foco después de que las tarjetas vuelvan a ser visibles.
      if (restoreFocus) this.menu.querySelector('a[href]')?.focus({ preventScroll: true });
      this.measure();
      if (animate) {
        const styles = getComputedStyle(this.section);
        const time = styles.getPropertyValue('--rv-menu-duration').trim();
        const options = {
          duration: parseFloat(time) * (time.endsWith('ms') ? 1 : 1000),
          easing: styles.getPropertyValue('--rv-menu-easing').trim() || 'ease'
        };
        const offsets = positions.map(({ element, top }) => ({ element, y: top - element.getBoundingClientRect().top }));
        for (const { element, y } of offsets) {
          if (Math.abs(y) < .5) continue;
          // Solo se anima la traslación; las medidas finales ya están aplicadas.
          this.playAnimation(element, [{ translate: `0 ${y}px` }, { translate: '0 0' }], options);
        }
        if (compact) this.playAnimation(this.hint, [
          { transform: `translateY(${this.menuReserve}px)`, opacity: 0 },
          { transform: 'translateY(0)', opacity: 1 }
        ], options);
      }
    }
    playAnimation(element, frames, options) {
      const animation = element.animate(frames, options);
      this.animations.add(animation);
      animation.addEventListener('finish', () => this.animations.delete(animation), { once: true });
    }
    stopAnimations() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }
    setMenuOpen(open) {
      this.menuOpen = this.compact && open;
      const expanded = !this.compact || this.menuOpen;
      this.toggleAttribute('data-rv-menu-open', this.menuOpen);
      this.toggle.setAttribute('aria-expanded', String(expanded));
      this.menu.setAttribute('aria-hidden', String(!expanded));
      this.menu.toggleAttribute('inert', !expanded);
      setText(this.toggle.querySelector('[data-rv-menu-label]'), this.menuOpen ? 'Ocultar menú' : 'Mostrar menú');
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.restoreInputController?.abort();
      this.unsubscribe?.();
      this.resizeObserver?.disconnect();
      this.stopAnimations();
      cancelAnimationFrame(this.readyFrame);
      this.marker?.remove();
      this.section?.style.removeProperty('--rv-header-menu-height');
      this.section?.style.removeProperty('--rv-header-menu-reserve');
      this.menuHeight = this.menuReserve = undefined;
      root.classList.remove('rv-sticky-header-enabled');
      window.rvStickyLayout.setHeaderHeight(0);
    }
  }

  class StickyFilterModal extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.header = document.querySelector('.section-header');
      this.filterTop = null;
      this.panel = this.section?.querySelector('#mobile-facets');
      this.grid = this.section?.querySelector('#ProductGridContainer');
      this.trigger = this.querySelector('button');
      if (!this.panel || !this.grid || !this.trigger) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.trigger.addEventListener('click', () => this.openFilters(), options);
      this.panel.addEventListener('toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.panel.open));
        this.panel.querySelector('summary').setAttribute('aria-expanded', String(this.panel.open));
        request();
        if (!this.panel.open && this.openedFromBar) {
          this.openedFromBar = false;
          delete this.panel.rvStickyOpener;
          requestAnimationFrame(() => this.trigger.focus({ preventScroll: true }));
        }
      }, options);
      this.resizeObserver = new ResizeObserver(request);
      this.resizeObserver.observe(this.grid);
      this.resizeObserver.observe(this.panel.querySelector('summary'));
      this.gridObserver = new MutationObserver(() => { this.syncLabels(); request(); });
      this.gridObserver.observe(this.grid, { childList: true });
      this.unsubscribe = window.rvStickyLayout.subscribe(state => this.update(state));
      this.syncLabels();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.gridObserver?.disconnect();
      this.unsubscribe?.();
      if (this.panel) delete this.panel.rvStickyOpener;
      this.style.removeProperty('--rv-filter-top');
    }
    syncLabels() {
      const active = this.section.querySelector('.active-facets-mobile') || this.section.querySelector('.active-facets-desktop');
      const count = active?.querySelectorAll('.active-facets__button').length || 0;
      const badge = this.querySelector('[data-rv-filter-count]');
      setText(badge, String(count));
      badge.hidden = count === 0;
      const sort = this.panel.querySelector('[name="sort_by"]');
      const appliedSort = new URLSearchParams(location.search).get('sort_by') || sort?.value;
      const option = sort && Array.from(sort.options || []).find(item => item.value === appliedSort);
      setText(this.querySelector('[data-rv-filter-sort]'), option?.textContent.trim() || '');
    }
    openFilters() {
      if (this.panel.open) return;
      this.openedFromBar = true;
      this.panel.rvStickyOpener = this.trigger;
      const scrollTop = window.scrollY;
      document.body.setAttribute('data-top', scrollTop);
      document.body.style.top = `-${scrollTop}px`;
      document.body.classList.add('fixed-position', 'overflow-hidden-tablet');
      this.panel.open = true;
      this.panel.classList.add('menu-opening');
      this.panel.querySelector('summary').setAttribute('aria-expanded', 'true');
      const close = this.panel.querySelector('.mobile-facets__close');
      if (typeof trapFocus === 'function') trapFocus(this.panel, close || this.panel.querySelector('summary'));
      request();
    }
    update(state) {
      if (!this.panel || !this.grid) return;
      if (!state.mobile || state.blocked) { setVisible(this, false); return; }
      if (!this.header?.isConnected) this.header = document.querySelector('.section-header');
      // Al volver arriba, la franja de anuncios vuelve a desplazar el header.
      // Usar su borde real, no solo su altura, también durante la salida.
      const top = Math.max(0, Math.ceil(this.header?.getBoundingClientRect().bottom ?? state.headerHeight));
      const barHeight = this.getBoundingClientRect().height;
      const pastButton = this.panel.querySelector('summary').getBoundingClientRect().bottom <= top;
      const inCollection = this.grid.getBoundingClientRect().bottom > top + barHeight;
      if (top !== this.filterTop) {
        this.filterTop = top;
        this.style.setProperty('--rv-filter-top', `${top}px`);
      }
      setVisible(this, pastButton && inCollection);
    }
  }

  class StickyCartModal extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.mainButton = this.form?.querySelector('[name="add"]');
      if (!this.mainButton) return;
      document.body.classList.add('rv-has-sticky-cart');
      this.controller = new AbortController();
      this.addEventListener('click', event => {
        if (event.target.closest('[data-rv-cart-submit]')) this.returnFocus = true;
      }, { signal: this.controller.signal });
      this.formObserver = new MutationObserver(() => { this.syncButton(); request(); });
      this.formObserver.observe(this.form.closest('product-form') || this.form, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'class']
      });
      // El tema sustituye el interior de esta barra al cambiar de variante.
      this.contentObserver = new MutationObserver(() => { this.syncButton(); request(); });
      this.contentObserver.observe(this, { childList: true });
      this.resizeObserver = new ResizeObserver(() => {
        root.style.setProperty('--rv-sticky-cart-height', `${Math.ceil(this.getBoundingClientRect().height)}px`);
        request();
      });
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.mainButton);
      this.unsubscribe = window.rvStickyLayout.subscribe(state => this.update(state));
      this.syncButton();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.formObserver?.disconnect();
      this.contentObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.unsubscribe?.();
      document.body.classList.remove('rv-has-sticky-cart', 'modal__sticky-cart');
      root.style.removeProperty('--rv-sticky-cart-height');
    }
    syncButton() {
      const button = this.querySelector('[data-rv-cart-submit]');
      if (!button || !this.mainButton) return;
      const busy = this.mainButton.getAttribute('aria-disabled') === 'true';
      button.disabled = this.mainButton.disabled || busy;
      button.classList.toggle('loading', busy);
      button.setAttribute('aria-busy', String(busy));
      button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !busy);
      const label = this.mainButton.disabled
        ? this.mainButton.querySelector('span')?.textContent.trim() || window.variantStrings?.soldOut || 'Agotado'
        : document.documentElement.lang.startsWith('es') ? 'Agregar' : window.variantStrings?.addToCart || 'Add to cart';
      setText(button.querySelector('[data-rv-cart-label]'), label);
      button.setAttribute('aria-label', this.mainButton.disabled ? label : window.variantStrings?.addToCart || label);
      const errorSource = this.form.closest('product-form')?.querySelector('.product-form__error-message-wrapper:not([hidden]) .product-form__error-message');
      const error = this.querySelector('[data-rv-cart-error]');
      if (error) {
        const message = errorSource?.textContent.trim() || '';
        setText(error, message);
        error.hidden = !message;
        if (message) this.returnFocus = false;
      }
    }
    update(state) {
      if (!this.mainButton) return;
      const visible = !state.blocked && this.mainButton.getBoundingClientRect().bottom <= state.headerHeight;
      setVisible(this, visible);
      document.body.classList.toggle('modal__sticky-cart', visible);
      if (this.wasBlocked && !state.blocked && visible && this.returnFocus) {
        this.returnFocus = false;
        const active = document.activeElement;
        const closedDrawer = active?.closest('cart-drawer:not(.active)');
        if (active === document.body || closedDrawer) this.querySelector('[data-rv-cart-submit]')?.focus({ preventScroll: true });
      }
      this.wasBlocked = state.blocked;
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);
  if (!customElements.get('sticky-filter-modal')) customElements.define('sticky-filter-modal', StickyFilterModal);
  if (!customElements.get('sticky-cart-modal')) customElements.define('sticky-cart-modal', StickyCartModal);
})();
